import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from '@mulesoft/anypoint-components/lib/Select';
import Radio from '@mulesoft/anypoint-components/lib/Radio';
import RadioGroup from '@mulesoft/anypoint-components/lib/RadioGroup';
import { RUN_TIME_OPTIONS } from '../constants';
import { generateRunFrequencyOptions, generateScheduleId } from '../utils';
import ScheduledScanSwitch from './ScheduledScanSwitch';
import styles from '../ScannerConfigurationForm.css';

/**
 * ScheduledRunSection Component
 *
 * Renders the scheduled run configuration section with:
 * - Scheduled scan toggle switch (with status indicator)
 * - Run frequency selector (conditionally rendered)
 * - Run time selector (conditionally rendered)
 * - Sync review options (conditionally rendered)
 */
const ScheduledRunSection = ({
  status,
  runFrequency,
  at,
  syncReview,
  onChange,
  onStatusToggle
}) => {
  const runFrequencyOptions = useMemo(
    () => generateRunFrequencyOptions(runFrequency),
    [runFrequency]
  );

  // Convert runFrequency object to string identifier for Select component
  // Select component only supports string/number values, not objects
  const selectedValue = useMemo(() => {
    // If runFrequency is null/undefined, return undefined so Select shows placeholder
    if (!runFrequency || runFrequency === 'Custom') {
      return undefined;
    }

    // If runFrequency is an object, convert it to a string identifier
    if (typeof runFrequency === 'object' && runFrequency !== null) {
      return generateScheduleId(runFrequency);
    }

    // If it's a string (backward compatibility), use it directly
    return runFrequency;
  }, [runFrequency]);

  // Only show schedule fields when status is SCHEDULED
  const showScheduleFields = status === 'SCHEDULED';

  return (
    <section className={styles.section}>
      <div className={styles['section-title']}>Scanner Run Configuration</div>

      {/* Scheduled Scan Toggle Field */}
      <div className={styles.field}>
        <label htmlFor="scheduled-scan-toggle" className={styles.label}>
          Enable Scheduled Scan
        </label>
        <div className={styles['field-content']}>
          <ScheduledScanSwitch status={status} onToggle={onStatusToggle} />
        </div>
      </div>

      {/* Conditionally render schedule fields - unmount when not SCHEDULED */}
      {showScheduleFields && (
        <React.Fragment>
          {/* Run Schedule Field */}
          <div className={styles.field}>
            <label htmlFor="run-frequency" className={styles.label}>
              Run Schedule
            </label>
            <div className={styles['field-content']}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%'
                }}
              >
                <Select
                  id="run-frequency"
                  name="run-frequency"
                  testId="run-frequency"
                  value={selectedValue}
                  options={runFrequencyOptions}
                  placeholder="Select..."
                  onChange={(event) => {
                    const selectedOptionValue = event.itemsSelected?.value;

                    if (!selectedOptionValue) {
                      onChange('runFrequency', '');

                      return;
                    }

                    // Find the option and get the schedule object
                    const selectedOption = runFrequencyOptions.find(
                      (opt) => opt.value === selectedOptionValue
                    );

                    // Pass the schedule object (or string for 'Custom') to onChange
                    if (selectedOption) {
                      onChange(
                        'runFrequency',
                        selectedOption.scheduleObj || selectedOptionValue
                      );
                    } else {
                      onChange('runFrequency', selectedOptionValue);
                    }
                  }}
                />
                <span className={styles['schedule-separator']}>at</span>
                <Select
                  id="run-time"
                  name="run-time"
                  testId="run-time"
                  value={at}
                  options={RUN_TIME_OPTIONS}
                  onChange={(event) =>
                    onChange('at', event.itemsSelected?.value || '')
                  }
                />
              </div>
            </div>
          </div>

          {/* Sync Review Field */}
          <div className={styles.field}>
            <label htmlFor="sync-review" className={styles.label}>
              Sync Review
            </label>
            <div className={styles['field-content']}>
              <RadioGroup
                id="sync-review"
                name="syncReview"
                testId="sync-review"
                value={syncReview}
                orientation="horizontal"
                onChange={(event) => onChange('syncReview', event.value)}
              >
                <Radio
                  testId="sync-review-auto-resolve"
                  label="Auto-resolve"
                  value="auto-resolve"
                  style={{ marginRight: '110px' }}
                />
                <Radio
                  testId="sync-review-ask-for-review"
                  label="Ask to review"
                  value="ask-for-review"
                  disabled
                />
              </RadioGroup>
            </div>
          </div>
        </React.Fragment>
      )}
    </section>
  );
};

ScheduledRunSection.propTypes = {
  /** Current status of the scheduled scan */
  status: PropTypes.oneOf(['SCHEDULED', 'PAUSED', 'STOPPED']).isRequired,
  /** Current run frequency value (string for backward compatibility or object) */
  runFrequency: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      unit: PropTypes.string,
      interval: PropTypes.number,
      on: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ]).isRequired,
  /** Current run time value (at) */
  at: PropTypes.string.isRequired,
  /** Current sync review value */
  syncReview: PropTypes.string.isRequired,
  /** Handler for field changes */
  onChange: PropTypes.func.isRequired,
  /** Handler for status toggle */
  onStatusToggle: PropTypes.func.isRequired
};

export default ScheduledRunSection;
